import { useBrandingContext } from 'app-context/BrandingsContext';
import React, { useState, useRef, useEffect } from 'react';

const useAudioRecorder = (onStop) => {
  const { brandingData } = useBrandingContext();
  const { sideBar_PrimaryColor } = brandingData;
  const mediaRecorderRef = useRef(null);
  const analyserRef = useRef(null);
  const audioChunksRef = useRef([]);
  const animationIdRef = useRef(null);
  const canvasRef = useRef(null);
  const [isRecording, setIsRecording] = useState(false);

  // Tracks current drawing X position
  const xRef = useRef(0);

  // Draw waveform bars based on live audio input
  const drawWaveform = (analyser, canvas) => {
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const bufferLength = analyser.fftSize;
    const dataArray = new Uint8Array(bufferLength);

    const barWidth = 1.5; // Width of each vertical bar
    const barGap = 2; // Gap between bars
    const minHeight = 2; // Minimum bar height

    const draw = () => {
      analyser.getByteTimeDomainData(dataArray);

      const value = dataArray[0] / 128.0;
      const rawHeight = Math.abs(value - 1) * canvas.height;
      const height = Math.max(rawHeight, minHeight);
      const y = (canvas.height - height) / 2;

      // Clear the bar area
      ctx.fillStyle = '#fbfbfb';
      ctx.fillRect(xRef.current, 0, barWidth, canvas.height);

      // Draw the new bar
      ctx.fillStyle = sideBar_PrimaryColor ?? 'black';
      ctx.fillRect(xRef.current, y, barWidth, height);

      xRef.current += barWidth + barGap;

      // Auto-grow canvas first
      if (xRef.current + barWidth >= canvas.width) {
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = canvas.width;
        tempCanvas.height = canvas.height;
        tempCanvas.getContext('2d').drawImage(canvas, 0, 0);

        canvas.width += 20;
        ctx.drawImage(tempCanvas, 0, 0);
      }

      if (canvas.parentElement) {
        canvas.parentElement.scrollLeft = canvas.parentElement.scrollWidth;
      }

      animationIdRef.current = setTimeout(draw, 200);
    };

    draw();
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      const audioContext = new AudioContext();
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();

      analyser.fftSize = 1024;
      source.connect(analyser);

      mediaRecorderRef.current = mediaRecorder;
      analyserRef.current = analyser;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        onStop(blob);
      };

      mediaRecorder.start();
      setIsRecording(true);

      if (canvasRef.current) {
        xRef.current = 0;
        drawWaveform(analyser, canvasRef.current);
      }
    } catch (err) {
      console.error('Microphone access error:', err);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      clearTimeout(animationIdRef.current);
      setIsRecording(false);
    }
  };

  useEffect(() => {
    return () => {
      clearTimeout(animationIdRef.current);
    };
  }, []);

  return {
    isRecording,
    startRecording,
    stopRecording,
    canvasRef,
  };
};

export default useAudioRecorder;
